/**
 * VendorUploadStep - Step 2 of SMB Vendor Cost Import
 *
 * Upload vendor cost file (Excel with multiple tabs OK)
 * Polls for smartImport processing completion
 * Shows detected columns when ready
 */

import React, { useState } from 'react'
import { Space, Card, Alert, Button, Upload, Typography, List, Progress, Spin } from 'antd'
import {
  InboxOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { smbSyncService } from '../../../services/smbSyncService'
import { errorService } from '../../../services/errorService'
import { fileValidationService } from '../../../services/fileValidationService'
import type { StepProps } from './types'

const { Dragger } = Upload
const { Title, Text } = Typography

type UploadStatus = 'idle' | 'uploading' | 'processing' | 'success' | 'error'

/**
 * Step 2: Upload vendor cost file and wait for processing
 */
export const VendorUploadStep: React.FC<StepProps> = ({ state, onNext, onBack, onCancel }) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle')
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [detectedColumns, setDetectedColumns] = useState<string[]>([])
  const [detectedTabs, setDetectedTabs] = useState<string[]>([])
  const [progress, setProgress] = useState(0)

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    const uploadFile = file as File
    const fileName = uploadFile.name

    setUploadedFileName(fileName)
    setUploadStatus('uploading')
    setProgress(10)

    try {
      // Validate file extension
      const fileExtension = fileName.split('.').pop()?.toLowerCase()
      const allowedExtensions = ['xlsx', 'xls', 'xlsb', 'xlsm', 'csv', 'pdf']
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        // Use friendly file validation service instead of technical error
        setUploadStatus('error')
        setProgress(0)
        fileValidationService.showInvalidExtension(fileName, allowedExtensions, handleRetry)
        onError?.(new Error('Invalid file extension'))
        return
      }

      // Step 1: Get presigned URL
      setProgress(20)
      const presignResponse = await smbSyncService.uploadVendor(state.jobId, fileName)

      if (!presignResponse.success || !presignResponse.data) {
        setUploadStatus('error')
        setProgress(0)
        const errorMsg = presignResponse.message || 'Failed to get upload authorization'
        errorService.showError('Authorization Failed', errorMsg, presignResponse.errors)
        onError?.(new Error(errorMsg))
        return
      }

      const { put_url } = presignResponse.data

      // Step 2: Upload to S3
      setProgress(40)
      const uploadResponse = await smbSyncService.uploadToS3(put_url, uploadFile)

      if (!uploadResponse.success) {
        setUploadStatus('error')
        setProgress(0)
        const errorMsg = uploadResponse.message || 'Failed to upload file to S3'
        errorService.showError('S3 Upload Failed', errorMsg, uploadResponse.errors)
        onError?.(new Error(errorMsg))
        return
      }

      // Step 3: Poll for vendor processing to complete (S3 trigger handles processing)
      setUploadStatus('processing')
      setProgress(60)

      const pollResponse = await smbSyncService.pollUntilVendorReady(state.jobId)

      if (!pollResponse.success || !pollResponse.data) {
        // Explicitly set error state BEFORE showing error dialog
        setUploadStatus('error')
        setProgress(0)
        const errorMsg = pollResponse.message || 'Failed to process vendor file'

        // Check if this is a file content issue (user's file problem) vs system error
        // Check both error codes AND message content (backend may include code in message)
        const isFileIssue = pollResponse.errors?.some(
          (e) =>
            e.code === 'FORMAT_MISMATCH' ||
            e.code === 'INVALID_FORMAT' ||
            e.code === 'MISSING_COLUMNS' ||
            e.code === 'EMPTY_FILE' ||
            e.code === 'CORRUPTED_FILE' ||
            e.code === 'CSV_EMPTY' ||
            e.code === 'CSV_TOO_FEW_COLUMNS' ||
            e.code === 'CSV_NO_HEADER_ROW' ||
            e.code === 'CSV_DUPLICATE_COLUMNS' ||
            e.message?.includes('FORMAT_MISMATCH') ||
            e.message?.includes('CSV_')
        ) || errorMsg.includes('FORMAT_MISMATCH') || errorMsg.includes('format issue') ||
           errorMsg.includes('CSV_')

        if (isFileIssue) {
          // Use friendly file validation service for user file issues
          // Note: Backend message already contains actionable fix instructions
          fileValidationService.showProcessingIssue(
            fileName,
            errorMsg,
            undefined,
            handleRetry
          )
        } else {
          // Use technical error service for system errors
          errorService.showError('Vendor Processing Failed', errorMsg, pollResponse.errors)
        }

        onError?.(new Error(errorMsg))
        return // Exit early, don't continue
      }

      // Success! Check if Excel (tabs) or CSV (columns)
      const responseData = pollResponse.data
      const vendorTabs = responseData.vendor_tabs || []
      const vendorColumns = responseData.vendor_columns || []

      setProgress(100)
      setUploadStatus('success')
      setDetectedTabs(vendorTabs)
      setDetectedColumns(vendorColumns)
      onSuccess?.('ok')

      // Auto-advance: If multiple tabs detected, go directly to tab selection
      if (vendorTabs.length > 1) {
        onNext({
          vendorFile: { name: fileName } as File,
          vendorTabs: vendorTabs,
          currentStep: 'tabs',
        })
      }

    } catch (err: any) {
      // Ensure UI updates even on unexpected errors
      setUploadStatus('error')
      setProgress(0)
      const errorMsg = err.message || 'Upload failed'
      errorService.showError('Upload Failed', errorMsg)
      onError?.(err)
    }
  }

  const handleNext = () => {
    if (uploadStatus !== 'success') {
      errorService.showError(
        'Upload Required',
        'Please upload and process a vendor file before continuing',
        [{ code: 'NO_FILE_UPLOADED', message: 'Vendor file must be uploaded and processed first' }]
      )
      return
    }

    // Check if Excel file (has tabs) or CSV file (has columns)
    if (detectedTabs.length > 0) {
      // Excel file - go to tab selection step
      onNext({
        vendorFile: { name: uploadedFileName } as File,
        vendorTabs: detectedTabs,
        currentStep: 'tabs', // Explicitly set next step
      })
    } else {
      // CSV file - skip tab selection, go directly to field mapping
      // (sample data will be fetched in FieldMappingStep)
      onNext({
        vendorFile: { name: uploadedFileName } as File,
        currentStep: 'mapping', // Skip tabs step
      })
    }
  }

  const handleRetry = () => {
    setUploadStatus('idle')
    setUploadedFileName('')
    setDetectedColumns([])
    setDetectedTabs([])
    setProgress(0)
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Instructions */}
      <Card>
        <Title level={4}>Step 2: Upload Vendor Cost File</Title>
        <Text>
          Upload your vendor cost file (Excel, CSV, or PDF format). The file will be automatically processed to
          extract clean data. This may take a few moments.
        </Text>
      </Card>

      {/* Upload Zone */}
      <Card>
        {(uploadStatus === 'idle' || uploadStatus === 'error') && (
          <Dragger
            customRequest={handleUpload}
            accept=".xlsx,.xls,.xlsb,.xlsm,.csv,.pdf"
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drop your vendor file here</p>
            <p className="ant-upload-hint">or click to browse (Excel, CSV, or PDF)</p>
          </Dragger>
        )}

        {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
          <Card style={{ textAlign: 'center', padding: '24px' }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
              style={{ marginBottom: '16px' }}
            />
            <div style={{ marginBottom: '16px' }}>
              <Text strong>
                {uploadStatus === 'uploading' ? 'Uploading file...' : 'Processing vendor data...'}
              </Text>
              <br />
              <Text type="secondary">File: {uploadedFileName}</Text>
            </div>
            <Progress
              percent={progress}
              status="active"
              strokeColor="#52c41a"
              style={{ maxWidth: '300px', margin: '0 auto' }}
            />
            {uploadStatus === 'processing' && (
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary">
                  Extracting tabs, detecting headers, and cleaning data...
                </Text>
              </div>
            )}
          </Card>
        )}

        {uploadStatus === 'success' && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message="Vendor file processed successfully!"
              description={`File: ${uploadedFileName}`}
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />

            {/* Show detected tabs (Excel) */}
            {detectedTabs.length > 0 && (
              <div>
                <Text strong>Detected Tabs ({detectedTabs.length}):</Text>
                <List
                  size="small"
                  bordered
                  dataSource={detectedTabs}
                  style={{ marginTop: '8px', maxHeight: '200px', overflow: 'auto' }}
                  renderItem={(tab) => (
                    <List.Item>
                      <Space>
                        <FileExcelOutlined />
                        <Text>{tab}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
                <Alert
                  message="Next: Select which tabs to include in the import"
                  type="info"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              </div>
            )}

            {/* Show detected columns (CSV) */}
            {detectedColumns.length > 0 && detectedTabs.length === 0 && (
              <div>
                <Text strong>Detected Vendor Columns ({detectedColumns.length}):</Text>
                <List
                  size="small"
                  bordered
                  dataSource={detectedColumns}
                  style={{ marginTop: '8px', maxHeight: '200px', overflow: 'auto' }}
                  renderItem={(column) => (
                    <List.Item>
                      <Space>
                        <FileExcelOutlined />
                        <Text>{column}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Space>
        )}

        {uploadStatus === 'error' && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Button onClick={handleRetry}>Try Again</Button>
          </div>
        )}
      </Card>

      {/* Action buttons */}
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              size="large"
              onClick={onBack}
              disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}
            >
              Back
            </Button>
            <Button
              size="large"
              onClick={onCancel}
              disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}
            >
              Cancel
            </Button>
          </Space>
          <Button
            type="primary"
            size="large"
            onClick={handleNext}
            disabled={uploadStatus !== 'success'}
          >
            {detectedTabs.length > 0 ? 'Next: Select Tabs' : 'Next: Map Fields'}
          </Button>
        </Space>
      </Card>
    </Space>
  )
}
